import React, { FunctionComponent } from 'react';
import classnames from 'classnames';

import {
  AccordionText,
  Button,
  Heading,
  LinesPerBreakpoint,
  MediaPercentage,
  VisibleElementsOptions
} from '@savebetter/components';

import { MediaProps } from './Media';
import { Blok as NumberProps, Numbers } from './Numbers';
import NumberSectionTitle from './NumberSectionTitle';
import { PageSectionBlok, PageSectionProps } from './PageSection';
import { RichText, RichTextProps } from './RichText';
import { Section } from './Section';

interface Blok extends PageSectionBlok {
  introText: RichTextProps;
  introMedia: MediaProps[];
  heading?: string;
  description?: RichTextProps;
  buttonLabel?: string;
  buttonLink?: string;
  numbers?: NumberProps[];
  closeText?: RichTextProps;
}

interface AboutUsProps extends PageSectionProps {
  blok: Blok;
}

const renderRichTextWithMedia = (introText: Blok['introText'], introMedia: Blok['introMedia'] = []): JSX.Element => {
  const media = introMedia[0] || { img: '', alt: '' };

  return (
    <div className="flex flex-col-reverse md:flex-row">
      <RichText content={introText} className="w-full" />
      <div className="ml-0 md:ml-5 mb-10 md:mb-0 w-full">
        <MediaPercentage size="full" src={media.img} alt={media.alt} loading="lazy" />
      </div>
    </div>
  );
};

const renderHeading = (heading: Blok['heading']): JSX.Element | null =>
  heading ? (
    <Heading kind="3" color="text-primary" level="2" className="mt-8 md:mt-10">
      {heading}
    </Heading>
  ) : null;

const renderDescription = (description: Blok['description']): JSX.Element | null => {
  if (!description) {
    return null;
  }

  const linesPerBreakpoint = { sm: 5 } as LinesPerBreakpoint;
  const visibleElement = { sm: 'icon' } as VisibleElementsOptions;

  return (
    <AccordionText
      linesPerBreakpoint={linesPerBreakpoint}
      visibleElement={visibleElement}
      buttonClassName="mx-auto mt-6"
      addAccordion={true}
    >
      <RichText content={description} className="mt-6" />
    </AccordionText>
  );
};

const renderButton = (buttonLink: Blok['buttonLink'], buttonLabel: Blok['buttonLabel']): JSX.Element | null =>
  buttonLink && buttonLabel ? (
    <div className="flex justify-center">
      <Button href={buttonLink} className="sm:px-12 w-full md:w-auto my-12 lg:my-10">
        {buttonLabel}
      </Button>
    </div>
  ) : null;

const renderNumberSection = (numbers: Blok['numbers'] = [], sectionClassName?: string): JSX.Element | null =>
  numbers.length > 0 ? (
    <Numbers
      blok={numbers[0]}
      sectionClassName={classnames(
        sectionClassName,
        {
          'sb-section-has-border-top': numbers[0].hasBackground
        },
        '-mx-8',
        'pb-6'
      )}
      sectionTitleWrapper={NumberSectionTitle}
    />
  ) : null;

const renderCloseText = (closeText: Blok['closeText']): JSX.Element | null =>
  closeText ? <RichText content={closeText} className="mt-6 md:mt-12 mb-12 md:mb-6 lg:mb-12" /> : null;

export const AboutUs: FunctionComponent<React.PropsWithChildren<AboutUsProps>> = ({ blok, sectionClassName }) => {
  const {
    hasBackground,
    hasBorder,
    introText,
    introMedia = [],
    heading,
    description,
    buttonLabel,
    buttonLink,
    numbers = [],
    closeText,
    hasPaddingBottom,
    hasPaddingTop
  } = blok;
  return (
    <Section
      width="md"
      hasBackground={hasBackground}
      hasBorder={hasBorder}
      className={sectionClassName}
      blok={blok}
      hasPaddingBottom={hasPaddingBottom}
      hasPaddingTop={hasPaddingTop}
    >
      {renderRichTextWithMedia(introText, introMedia)}
      {renderHeading(heading)}
      {renderDescription(description)}
      {renderButton(buttonLink, buttonLabel)}
      {renderNumberSection(numbers, sectionClassName)}
      {renderCloseText(closeText)}
    </Section>
  );
};
